import React, { useState, useEffect, useRef } from "react";
import { UploadCloud, FileMusic, AlertCircle, ChevronDown, FileUp } from "lucide-react";
import { Dialog } from "../ui/Dialog.js";
import { Button } from "../ui/Button.js";
import { Badge } from "../ui/Badge.js";
import { Field, Input, Textarea } from "../ui/Field.js";
import { normalizeLyrics, TAB_EDIT_COPY } from "../../utils/tabEdits.js";
import { TuningPicker } from "../tuning/TuningPicker.js";
import { detectFileTuning, customTuningFromMidi } from "../../utils/gpTuning.js";
import { Link, useLocation } from "react-router-dom";
import { apiClient, ApiError } from "../../utils/apiClient.js";
import { useAuthStore } from "../../stores/authStore.js";
import { useToast } from "../ui/useToast.js";
import {
  saveLocalTab,
  findLocalTabByContentHash,
} from "../../db/operations.js";
import { accountId } from "../../db/scope.js";
import { newId } from "../../utils/id.js";
import { requestTabUploadSync } from "../../sync/tabUploadSync.js";
import {
  validateGpMeta,
  inspectGpFile,
  hashBuffer,
} from "../../utils/gpValidation.js";
import { TabSong, DifficultyLevel } from "../../types/index.js";
import { Segmented } from "../ui/Segmented.js";
import {
  DIFFICULTY_KIND_LONG_LABEL,
  DIFFICULTY_LEVELS,
  DIFFICULTY_UNRATED_LABEL,
  difficultyHint,
  difficultyLevelInfo,
  type DifficultyKind,
} from "../../utils/difficulty.js";
import { toPersianDigits } from "../../utils/timeUtils.js";

export interface TabImportDialogProps {
  open: boolean;
  onClose: () => void;
  onUploaded: (tab: TabSong) => void;
  defaultPublish?: boolean;
}

function describeUploadRateLimit(err: ApiError, suffix: string): string {
  const retry = err.data.retryAfter;
  const wait =
    typeof retry === "number" && retry > 0
      ? ` لطفاً ${Math.max(1, Math.ceil(retry / 60)).toLocaleString("fa-IR")} دقیقه دیگر دوباره تلاش کنید.`
      : " لطفاً کمی بعد دوباره تلاش کنید.";
  return `تعداد آپلودهای شما در این ساعت به حد مجاز (۱۰ آپلود در ساعت) رسیده است.${wait} ${suffix}`;
}

function describePublishError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 401) {
      return "نشست شما منقضی شده یا وارد نشده‌اید؛ برای انتشار وارد حساب کاربری شوید. تبلچر منتشر نشد.";
    }
    if (err.status === 429) return describeUploadRateLimit(err, "تبلچر منتشر نشد.");
    if (err.status === 413)
      return "حجم فایل از حد مجاز سرور بیشتر است. تبلچر منتشر نشد.";
    if (err.status === 400) {
      if (err.data.code === "EMPTY_FILE") return "فایل خالی است.";
      if (err.data.code === "INVALID_TAB_FILE")
        return "محتوای فایل معتبر نیست یا فایل Guitar Pro آسیب دیده است.";
      return "سرور فایل را نپذیرفت (فرمت یا اطلاعات نامعتبر). تبلچر منتشر نشد.";
    }
    if (err.status === 409) {
      if (err.data.code === "ALREADY_PUBLISHED")
        return "این فایل قبلاً در بازارچه منتشر شده است.";
      if (err.data.code === "ALREADY_PENDING")
        return "این فایل قبلاً برای بررسی ارسال شده و در انتظار تأیید است.";
      return "این فایل قبلاً در سرور ثبت شده است.";
    }
    if (err.status >= 500)
      return "خطای سرور رخ داد. تبلچر منتشر نشد؛ بعداً دوباره تلاش کنید.";
    return "ارسال تبلچر ناموفق بود و منتشر نشد.";
  }
  return "ارتباط با سرور برقرار نشد. تبلچر منتشر نشد.";
}

const DIFFICULTY_OPTIONS = [
  { value: "none", label: DIFFICULTY_UNRATED_LABEL },
  ...DIFFICULTY_LEVELS.map((l) => ({
    value: String(l.value),
    label: toPersianDigits(l.value),
  })),
];

/** FRE-188: optional rhythm/lead difficulty picker (default «نامشخص»). */
const DifficultyField: React.FC<{
  kind: DifficultyKind;
  value: DifficultyLevel | null;
  onChange: (v: DifficultyLevel | null) => void;
}> = ({ kind, value, onChange }) => {
  const info = difficultyLevelInfo(value);
  return (
    <div className="flex flex-col gap-1.5" data-testid={`import-difficulty-${kind}`}>
      <span className="text-xs font-medium text-base-content/80">
        {DIFFICULTY_KIND_LONG_LABEL[kind]}
      </span>
      <Segmented
        size="lg"
        fullWidth
        value={value === null ? "none" : String(value)}
        onChange={(v) => onChange(v === "none" ? null : (Number(v) as DifficultyLevel))}
        options={DIFFICULTY_OPTIONS.map((o) => ({
          ...o,
          testId: `import-difficulty-${kind}-${o.value}`,
        }))}
      />
      <span className="text-xs text-base-content/60" data-testid={`import-difficulty-hint-${kind}`}>
        {info ? `${info.label}: ${difficultyHint(kind, value)}` : DIFFICULTY_UNRATED_LABEL}
      </span>
    </div>
  );
};

export const TabImportDialog: React.FC<TabImportDialogProps> = ({
  open,
  onClose,
  onUploaded,
  defaultPublish = false,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState("");
  const [artist, setArtist] = useState("");
  const [artistError, setArtistError] = useState<string | null>(null);
  const artistInputRef = useRef<HTMLInputElement>(null);
  const [tuning, setTuning] = useState("E Standard");
  const [tuningHint, setTuningHint] = useState<string | null>(null);
  const tuningTouched = useRef(false);
  const [publish, setPublish] = useState(defaultPublish);
  const [rhythmDifficulty, setRhythmDifficulty] = useState<DifficultyLevel | null>(null);
  const [leadDifficulty, setLeadDifficulty] = useState<DifficultyLevel | null>(null);
  const [lyrics, setLyrics] = useState("");
  const [lyricsOpen, setLyricsOpen] = useState(false);
  const [lyricsError, setLyricsError] = useState<string | null>(null);
  const lyricsFileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [needsLogin, setNeedsLogin] = useState(false);
  // FND-28: content check on selection; an invalid file keeps save disabled.
  const [fileInvalid, setFileInvalid] = useState(false);
  const [fileChecking, setFileChecking] = useState(false);
  const selectionToken = useRef(0);
  const toast = useToast();
  const routerLocation = useLocation();
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (open) {
      setPublish(defaultPublish);
    } else {
      selectionToken.current += 1;
      setFileInvalid(false);
      setFileChecking(false);
      setFile(null);
      setTitle("");
      setArtist("");
      setArtistError(null);
      setTuning("E Standard");
      setTuningHint(null);
      tuningTouched.current = false;
      setRhythmDifficulty(null);
      setLeadDifficulty(null);
      setLyrics("");
      setErrorMsg(null);
      setNeedsLogin(false);
      setIsUploading(false);
      setIsDragging(false);
    }
  }, [open, defaultPublish]);

  const handleFileSelection = (selected: File) => {
    setNeedsLogin(false);
    const token = ++selectionToken.current;
    const metaError = validateGpMeta(selected);
    if (metaError) {
      setFile(null);
      setFileInvalid(true);
      setFileChecking(false);
      setErrorMsg(metaError);
      return;
    }
    setFile(selected);
    setErrorMsg(null);
    setFileInvalid(false);
    setFileChecking(true);
    // FRE-139: preselect the tuning the file really uses (the user can still change it)
    tuningTouched.current = false;
    setTuningHint(null);
    void selected.arrayBuffer().then(async (buf) => {
      const bytes = new Uint8Array(buf);
      let contentError: string | null = null;
      try {
        contentError = (await inspectGpFile(selected.name, buf)).error;
      } catch {
        contentError = null; // inspection unavailable: submit re-checks
      }
      if (token !== selectionToken.current) return; // a newer selection (or close) won
      setFileChecking(false);
      if (contentError) {
        setFileInvalid(true);
        setErrorMsg(contentError);
        return;
      }
      try {
        const alphaTab = await import("@coderline/alphatab");
        const score = alphaTab.importer.ScoreLoader.loadScoreFromBytes(
          bytes,
          new alphaTab.Settings()
        );
        if (score?.artist && score.artist.trim()) {
          setArtist((prev) => (prev.trim() ? prev : score.artist.trim()));
          setArtistError(null);
        }
        if (score?.title && score.title.trim()) {
          setTitle((prev) => (prev.trim() ? prev : score.title.trim()));
        }
      } catch {
        // Fallback to file name if alphaTab parsing fails
      }
      const found = await detectFileTuning(bytes);
      if (token !== selectionToken.current) return;
      if (!found || tuningTouched.current) return;
      if (found.tuning) {
        setTuning(found.tuning.name);
        setTuningHint("از روی فایل تشخیص داده شد");
      } else {
        setTuning(customTuningFromMidi(found.midiHighToLow));
        setTuningHint("کوک این فایل در فهرست نیست؛ به‌صورت کوک سفارشی ثبت شد");
      }
    });
    if (!title) {
      const cleanName = selected.name.replace(/\.(gp5|gpx|gp|gp4|gp3)$/i, "");
      setTitle(cleanName);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelection(e.dataTransfer.files[0]);
    }
  };

  const handleLyricsFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    e.target.value = '';
    if (!f.name.endsWith('.txt') && f.type !== 'text/plain') {
      setLyricsError(TAB_EDIT_COPY.c33);
      return;
    }
    try {
      const buf = await f.arrayBuffer();
      const decoded = new TextDecoder('utf-8', { fatal: true }).decode(buf);
      const norm = normalizeLyrics(decoded);
      if (norm.length > 50000) {
        setLyricsError('حداکثر ۵۰٬۰۰۰ کاراکتر مجاز است.');
      } else {
        setLyricsError(null);
      }
      setLyrics(decoded);
    } catch {
      setLyricsError(TAB_EDIT_COPY.c33);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (fileInvalid || fileChecking) return;
    if (!file) {
      setErrorMsg("لطفاً یک فایل تبلچر Guitar Pro انتخاب کنید.");
      return;
    }

    if (!artist.trim()) {
      setArtistError("نام هنرمند الزامی است.");
      artistInputRef.current?.focus();
      setErrorMsg("نام هنرمند الزامی است.");
      return;
    }

    if (!title.trim()) {
      setErrorMsg("نام قطعه الزامی است.");
      return;
    }

    const metaError = validateGpMeta(file);
    if (metaError) {
      setErrorMsg(metaError);
      return;
    }

    const { isAuthenticated, isGuest } = useAuthStore.getState();
    const canUseApi = isAuthenticated && !isGuest;

    if (publish && !canUseApi) {
      setNeedsLogin(true);
      setErrorMsg(
        "برای انتشار در بازارچه باید وارد حساب کاربری شوید. تبلچر منتشر نشد.",
      );
      return;
    }

    setIsUploading(true);
    setErrorMsg(null);
    setNeedsLogin(false);

    try {
      const buffer = await file.arrayBuffer();

      const { error: contentError, tracks } = await inspectGpFile(file.name, buffer);
      if (contentError) {
        setErrorMsg(contentError);
        return;
      }

      const hash = await hashBuffer(buffer);
      const existing = await findLocalTabByContentHash(
        hash,
        hashBuffer,
        // Same file AND same title/artist = a repeat import; same file under a new name is allowed.
        (t) =>
          t.title.trim().toLowerCase() === title.trim().toLowerCase() &&
          t.artist.trim().toLowerCase() === artist.trim().toLowerCase(),
      );
      if (existing) {
        setErrorMsg(
          `این فایل قبلاً با عنوان «${existing.title}» در کتابخانه شما وجود دارد و دوباره ذخیره نشد.`,
        );
        setIsUploading(false);
        return;
      }

      // FRE-142 D1: the client id becomes the server id (no remap later).
      const tabId = newId();
      const localTracks = tracks
        ? tracks.map((t) => ({
            id: newId(),
            tabId,
            trackIndex: t.trackIndex,
            name: t.name,
          }))
        : undefined;

      const newTab: TabSong = {
        id: tabId,
        title: title.trim(),
        artist: artist.trim(),
        tuning,
        fileUrl: "",
        status: publish ? "PENDING" : "PRIVATE",
        createdAt: new Date().toISOString(),
        isDownloaded: true,
        localBlob: buffer,
        tracks: localTracks,
        rhythmDifficulty,
        leadDifficulty,
        pendingDifficulty: 0,
        lyricsContent: normalizeLyrics(lyrics) || null,
      };

      const normLyrics = normalizeLyrics(lyrics);
      if (normLyrics.length > 50000) {
        setLyricsError('حداکثر ۵۰٬۰۰۰ کاراکتر مجاز است.');
        setLyricsOpen(true);
        setIsUploading(false);
        return;
      }

      if (publish) {
        // Publishing must really succeed: surface every API failure and save nothing on failure.
        if (!navigator.onLine) {
          setErrorMsg(
            "اتصال اینترنت برقرار نیست؛ تبلچر منتشر نشد. بعداً دوباره تلاش کنید.",
          );
          return;
        }
        const formData = new FormData();
        formData.append("id", tabId); // before `file`
        // FRE-188 TL-08: empty string = unrated; both fields go before `file`
        formData.append("rhythm_difficulty", rhythmDifficulty ? String(rhythmDifficulty) : "");
        formData.append("lead_difficulty", leadDifficulty ? String(leadDifficulty) : "");
        if (normLyrics) {
          formData.append("lyrics", normLyrics);
        }
        if (tracks !== null) {
          formData.append("tracks", JSON.stringify(tracks));
        }
        formData.append("file", file);
        formData.append("title", title.trim());
        formData.append("artist", artist.trim());
        formData.append("tuning", tuning);
        formData.append("publish", "true");
        try {
          const apiTab = await apiClient.post<TabSong>(
            "/api/tabs/upload",
            formData,
          );
          if (apiTab?.id) {
            newTab.id = apiTab.id;
            newTab.fileUrl = apiTab.fileUrl;
            newTab.status = apiTab.status ?? "PENDING";
            newTab.uploaderId = apiTab.uploaderId;
            newTab.rhythmDifficulty = apiTab.rhythmDifficulty ?? null;
            newTab.leadDifficulty = apiTab.leadDifficulty ?? null;
          }
        } catch (apiErr) {
          if (apiErr instanceof ApiError && apiErr.status === 400 && apiErr.data?.code === 'INVALID_LYRICS') {
            setLyricsError(apiErr.data.message || 'محتوای متن آهنگ معتبر نیست.');
            setLyricsOpen(true);
            return;
          }
          setErrorMsg(describePublishError(apiErr));
          setNeedsLogin(apiErr instanceof ApiError && apiErr.status === 401);
          return;
        }
        await saveLocalTab(newTab, buffer);
        toast.info(
          "تبلچر ارسال شد و در انتظار بررسی و تأیید مدیر است؛ پس از تأیید در بازارچه نمایش داده می‌شود.",
        );
      } else {
        // Private import: saved locally at once (works offline); the background queue backs it up (always private).
        newTab.pendingUpload = 1;
        newTab.localFileName = file.name;
        newTab.status = "PRIVATE";
        newTab.fileUrl = "";
        newTab.uploaderId = accountId();
        await saveLocalTab(newTab, buffer);
        requestTabUploadSync();
      }

      onUploaded(newTab);
      onClose();
    } catch (err) {
      console.error("Failed to import tab:", err);
      setErrorMsg("خطا در ذخیره‌سازی یا ارسال فایل تبلچر.");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="آپلود تبلچر گیتار پرو (.gp5)"
      titleHeadingLevel="h3"
      description="فایل روی همین دستگاه ذخیره می‌شود."
      size="md"
      data-testid="tab-import-dialog"
      footer={
        <>
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={isUploading}
            disabled={fileInvalid || fileChecking}
            data-testid="dialog-primary-action"
          >
            ذخیره و آپلود
          </Button>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={isUploading}
            data-testid="dialog-secondary-action"
          >
            انصراف
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        {errorMsg && (
          <div
            role="alert"
            className="flex items-center gap-2 p-3 text-xs rounded-xl bg-error/15 border border-error/30 text-error-soft"
          >
            <AlertCircle className="size-4 shrink-0" />
            <span>
              {errorMsg}
              {needsLogin && (
                <>
                  {" "}
                  <Link to={`/auth?from=${encodeURIComponent(routerLocation.pathname + routerLocation.search)}`} className="link link-primary font-semibold">
                    ورود به حساب
                  </Link>
                </>
              )}
            </span>
          </div>
        )}

        {/* Dropzone */}
        <label
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-2xl p-6 flex flex-col items-center justify-center cursor-pointer transition-colors text-center ${
            isDragging
              ? "border-primary bg-primary/10"
              : "border-base-300 hover:border-primary/60 bg-base-100/50"
          }`}
        >
          <input
            type="file"
            accept=".gp5,.gpx,.gp,.gp4,.gp3"
            onChange={(e) => {
              if (e.target.files && e.target.files[0]) {
                handleFileSelection(e.target.files[0]);
              }
            }}
            className="hidden"
          />
          <div className="size-11 rounded-xl bg-primary/15 text-primary flex items-center justify-center mb-2">
            {file ? (
              <FileMusic className="size-5" />
            ) : (
              <UploadCloud className="size-5" />
            )}
          </div>
          {file ? (
            <div>
              <p
                dir="auto"
                className="font-bold text-sm text-primary break-all"
              >
                {file.name}
              </p>
              <p
                dir="ltr"
                className="text-xs text-base-content/60 font-mono mt-0.5"
              >
                {(file.size / 1024).toFixed(1)} KB
              </p>
            </div>
          ) : (
            <div>
              <p className="font-semibold text-sm text-base-content">
                فایل را اینجا رها کنید یا انتخاب کنید
              </p>
              <p
                dir="ltr"
                className="text-[11px] text-base-content/50 font-mono mt-1"
              >
                .gp3 .gp4 .gp5 .gpx .gp
              </p>
            </div>
          )}
        </label>

        {/* Title and Artist */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="نام قطعه" required>
            <Input
              type="text"
              placeholder="Master of Puppets"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              dir="ltr"
              required
            />
          </Field>

          <Field label="هنرمند" required error={artistError || undefined}>
            <Input
              ref={artistInputRef}
              type="text"
              placeholder="Metallica"
              value={artist}
              onChange={(e) => {
                setArtist(e.target.value);
                if (artistError) setArtistError(null);
              }}
              error={Boolean(artistError)}
              className={artistError ? "input-error border-error" : ""}
              dir="ltr"
            />
          </Field>
        </div>

        {/* Tuning */}
        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-base-content/80">
            نوع گیتار و کوک
          </span>
          <TuningPicker
            value={tuning}
            onChange={(name) => {
              tuningTouched.current = true;
              setTuningHint(null);
              setTuning(name);
            }}
            hint={tuningHint}
            testIdPrefix="import-tuning"
          />
        </div>

        {/* Difficulty (optional, FRE-188) */}
        <div className="flex flex-col gap-3">
          <DifficultyField kind="rhythm" value={rhythmDifficulty} onChange={setRhythmDifficulty} />
          <DifficultyField kind="lead" value={leadDifficulty} onChange={setLeadDifficulty} />
        </div>

        {/* Optional Lyrics Section (FRE-224 / FE-TE-07) */}
        <div className="flex flex-col gap-2">
          <button
            type="button"
            aria-expanded={lyricsOpen}
            aria-controls="import-lyrics-panel"
            onClick={() => setLyricsOpen((v) => !v)}
            data-testid="import-lyrics-toggle"
            className="w-full h-11 flex items-center justify-between rounded-xl px-3 bg-base-100 border border-base-300 text-[13px] font-medium text-base-content/80 hover:border-base-content/30 transition-colors duration-150 cursor-pointer"
          >
            <div className="flex items-center gap-2">
              <ChevronDown
                className={`size-4 transition-transform duration-150 ${lyricsOpen ? 'rotate-180' : ''}`}
              />
              <span>{TAB_EDIT_COPY.c34Header}</span>
            </div>
            {!lyricsOpen && lyrics.trim() && (
              <Badge tone="neutral" size="sm">
                {toPersianDigits(normalizeLyrics(lyrics).length)}
              </Badge>
            )}
          </button>

          {lyricsOpen && (
            <div id="import-lyrics-panel" data-testid="import-lyrics-panel" className="flex flex-col gap-2 pt-1">
              <Textarea
                dir="auto"
                rows={6}
                spellCheck={false}
                value={lyrics}
                onChange={(e) => {
                  setLyrics(e.target.value);
                  const norm = normalizeLyrics(e.target.value);
                  if (norm.length > 50000) {
                    setLyricsError('حداکثر ۵۰٬۰۰۰ کاراکتر مجاز است.');
                  } else if (lyricsError) {
                    setLyricsError(null);
                  }
                }}
                placeholder="{section: Verse}&#10;@1  [Am]Hello darkness my [G]old friend&#10;@5  [C]سلام ای [F]دوست قدیمی"
                data-testid="import-lyrics-text"
                className="leading-6"
              />

              <div className="flex items-center justify-between gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  leadingIcon={<FileUp className="size-4" />}
                  onClick={() => lyricsFileInputRef.current?.click()}
                  data-testid="import-lyrics-file"
                >
                  {TAB_EDIT_COPY.c34Button}
                </Button>
                <input
                  ref={lyricsFileInputRef}
                  type="file"
                  accept=".txt,text/plain"
                  onChange={(e) => void handleLyricsFile(e)}
                  className="hidden"
                />

                <span
                  data-testid="import-lyrics-counter"
                  className={`text-xs ${
                    normalizeLyrics(lyrics).length > 50000
                      ? 'text-error-soft font-semibold'
                      : 'text-base-content/60'
                  }`}
                >
                  {TAB_EDIT_COPY.c34Counter(normalizeLyrics(lyrics).length)}
                </span>
              </div>

              {lyricsError && (
                <p
                  role="alert"
                  data-testid="import-lyrics-error"
                  className="text-xs text-error-soft"
                >
                  {lyricsError}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Marketplace Publish Option */}
        <label className="flex items-center gap-2.5 pt-1 text-xs text-base-content/80 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={publish}
            onChange={(e) => setPublish(e.target.checked)}
            className="checkbox checkbox-primary checkbox-sm rounded-md"
          />
          <span>برای انتشار در بازارچه هم ارسال شود</span>
        </label>
      </form>
    </Dialog>
  );
};
